'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useState } from 'react';
import { BaseFillFolder } from '@/app/components/icons/BaseFillFolder';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLineMarket } from '@/app/components/icons/BaseLineMarket';
import { ProductLineMark } from '@/app/components/icons/ProductLineMark';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { DownloadButton, type WatermarkMode } from '@/app/components/shared/DownloadButton';
import { Avatar } from '@/app/components/shared/UserAvatar';
import { Button } from '@/app/components/ui/button';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import { Input } from '@/app/components/ui/input';
import { SITE, TEXT_2_AUDIO_IMAGE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { cn, getAudioImageIndex } from '@/app/utils';
import { CREATION_TYPE_TO_I18N_KEY } from '../_constants';
import { useRenameFile } from '../_hooks/use-rename-file';
import type { FileOperation, FileOperationType } from '../_utils/file-operations-config';
import { getContextOperations } from '../_utils/file-operations-config';
import type { FileItem } from '../types';
import { DraggableItem } from './DraggableItem';
import { FileContextMenu } from './FileContextMenu';

interface FileListItemProps {
  item: FileItem;
  isTeamSpace?: boolean;
  isSelected: boolean;
  isDraggingAny: boolean;
  moving?: boolean;
  selectedItems: string[];
  files: FileItem[];
  isOperationLoading: FileOperationType | null;
  onFileClick: (itemId: string, event?: React.MouseEvent) => void;
  onSelectItem: (itemId: string) => void;
  onOperation: (operation: FileOperationType, item: FileItem | FileItem[]) => void;
  // 文件状态更新回调
  onFilesUpdate?: (updater: (files: FileItem[]) => FileItem[]) => void;
}

export const FileListItem: FC<FileListItemProps> = ({
  item,
  isTeamSpace = false,
  isSelected,
  isDraggingAny,
  moving = false,
  selectedItems,
  files,
  isOperationLoading,
  onFileClick,
  onSelectItem,
  onOperation,
  onFilesUpdate,
}) => {
  const format = useFormatter();
  const t = useTranslations();
  const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);
  const userInfo = useStore((state) => state.userInfo);
  const {
    isEditingName,
    newName,
    renamingFile,
    editingRef,
    handleEditName,
    handleNameChange,
    handleKeyDown,
    handleRename,
  } = useRenameFile({ item, onFilesUpdate });

  const handleFileClick = (event: React.MouseEvent) => {
    if (isEditingName || isHoverCardOpen) return;
    onFileClick(item.id, event);
  };

  const renderOperationButton = (operation: FileOperation) => {
    // 特殊处理下载按钮，添加水印选项
    if (operation.id === 'download') {
      let watermarkMode: WatermarkMode = 'both';
      // 国内站 & (音频 / 免费用户) => 带水印
      if (SITE === 'china' && (item.creationType === 'audio' || userInfo?.subs_plan === 'free')) {
        watermarkMode = 'watermarkOnly';
      } else if (SITE === 'global' && item.creationType === 'audio') {
        // 海外站 & 音频 => 只有不带水印的
        watermarkMode = 'noWatermarkOnly';
      }
      return (
        <DownloadButton
          creationType={item.creationType}
          key={operation.id}
          className="h-7 rounded-8 bg-system-white10 px-3 hover:bg-system-white20"
          waterMarkUri={item.downloadUrl || ''}
          watermarkMode={watermarkMode}
          noWaterMarkUri={item.nomark_uri}
          taskId={''}
          creationId={item.creationId || ''}
          onHoverCardOpenChange={setIsHoverCardOpen}
          hasCopyright={item.hasCopyright}
        />
      );
    }

    // 其他操作按钮的默认渲染
    return (
      <CustomTooltip content={t(`creations.operations.${operation.id}`)} key={operation.id}>
        <Button
          className="h-7 rounded-8 bg-system-white10 px-3 hover:bg-system-white20"
          onClick={(e) => {
            e.stopPropagation();
            if (operation.id === 'rename') {
              handleEditName();
            } else {
              onOperation(operation.id, item);
            }
          }}
        >
          <operation.icon className="text-base text-system-white" />
        </Button>
      </CustomTooltip>
    );
  };

  return (
    <DraggableItem
      item={item}
      isSelected={isSelected}
      isDraggingAny={isDraggingAny}
      droppable={item.type === 'folder'}
      disabled={moving}
    >
      <FileContextMenu
        item={item}
        isLoading={isOperationLoading}
        onOperation={(operation, item) => {
          if (operation === 'rename') {
            handleEditName();
            return;
          }
          // 如果多选，则是针对多选的文件进行操作
          if (selectedItems.length > 1 && isSelected) {
            const selectedFiles = files.filter((file) => selectedItems.includes(file.id));
            onOperation(operation, selectedFiles);
          } else {
            onOperation(operation, item);
          }
        }}
        isBatch={selectedItems.length > 1 && isSelected}
      >
        <div
          data-item-id={item.id}
          className={cn(
            'group grid h-[50px] cursor-pointer items-center rounded-4 transition-all hover:bg-system-white24 sm:grid-cols-[32px_1fr_64px_56px]',
            isTeamSpace
              ? 'grid-cols-[32px_1fr_180px_128px_128px_120px] lg:grid-cols-[32px_1fr_148px_106px_106px_98px]'
              : 'grid-cols-[32px_1fr_128px_128px_120px] lg:grid-cols-[32px_1fr_106px_106px_98px]',
            isSelected && 'bg-system-white24',
          )}
          onClick={handleFileClick}
        >
          {/* Checkbox */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              onSelectItem(item.id);
            }}
            className="flex h-full w-8 items-center px-2"
          >
            <CheckboxFill
              checked={isSelected}
              className={cn('text-base opacity-0 group-hover:opacity-100', selectedItems.length > 0 && 'opacity-100')}
            />
          </div>

          {/* File name with icon */}
          <div className="flex min-w-0 items-center gap-1 sm:pr-2">
            <div className="flex-shrink-0">
              {moving ? (
                <BaseLineLoading2 className="size-5 animate-spin" />
              ) : item.type === 'folder' ? (
                <BaseFillFolder className="size-5 text-blue-400" />
              ) : (
                <div className="flex size-5 items-center justify-center">
                  <img
                    src={
                      item.creationType === 'audio'
                        ? TEXT_2_AUDIO_IMAGE[getAudioImageIndex(item.id, TEXT_2_AUDIO_IMAGE.length)].cover[SITE]
                        : item.coverUrl
                    }
                    alt={item.name}
                    className="size-full rounded-2 object-cover"
                  />
                </div>
              )}
            </div>

            {isEditingName ? (
              <div ref={editingRef} className="flex-1">
                <Input
                  autoFocus
                  className="h-[30px] max-w-[200px] flex-1 rounded-4 border border-[#343434] bg-[#20232A] px-3 font-medium text-sm text-system-white"
                  value={newName}
                  disabled={renamingFile}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleRename}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
            ) : (
              <>
                <span className="flex-1">
                  <CustomTooltip
                    content={item.name}
                    showOnlyOnOverflow
                    maxLines={1}
                    contentClassName="[word-break:break-word]"
                  >
                    <span className="font-medium text-sm text-system-white">{item.name}</span>
                  </CustomTooltip>
                </span>

                {/* 操作按钮 */}
                <div
                  className={cn(
                    'mr-1 hidden items-center gap-2 transition-opacity md:hidden md:group-hover:hidden',
                    isHoverCardOpen ? 'flex' : 'group-hover:flex',
                  )}
                >
                  {getContextOperations(false, item.type === 'folder').map((operation) => {
                    return renderOperationButton(operation);
                  })}
                </div>

                {/* 收藏按钮 */}
                <div
                  className={cn(
                    'mr-4 items-center transition-opacity md:hidden md:group-hover:hidden',
                    item.is_favorite || isHoverCardOpen ? 'flex' : 'hidden group-hover:flex',
                  )}
                >
                  <CustomTooltip
                    content={item.is_favorite ? t('creations.operations.unmark') : t('creations.operations.mark')}
                  >
                    <Button
                      className="h-7 rounded-8 bg-system-white10 px-3 hover:bg-system-white20"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOperation('mark', item);
                      }}
                    >
                      {item.is_favorite ? (
                        <BaseLineMarket className="text-[#FFCC00] text-base" />
                      ) : (
                        <ProductLineMark className="text-base text-system-white" />
                      )}
                    </Button>
                  </CustomTooltip>
                </div>
              </>
            )}
          </div>

          {/* Creator */}
          {isTeamSpace && (
            <div className="flex min-w-0 items-center gap-2 sm:hidden">
              {item.owner_id ? (
                <>
                  <Avatar
                    id={item.owner_id}
                    nickname={item.owner_nickname}
                    url={item.owner_avatar}
                    className="size-5"
                    nameClassName="text-[10px]"
                  />
                  <CustomTooltip
                    content={item.owner_nickname || item.owner_id}
                    showOnlyOnOverflow
                    maxLines={1}
                    contentClassName="[word-break:break-word]"
                  >
                    <span className="truncate text-sm text-system-white64">{item.owner_nickname || item.owner_id}</span>
                  </CustomTooltip>
                </>
              ) : (
                <span className="text-sm text-system-white64">--</span>
              )}
            </div>
          )}

          {/* Size */}
          <div className="text-sm">{item.size || '--'}</div>

          {/* Type */}
          <div className="text-sm">
            {item.type === 'folder'
              ? t('creations.filters.fileTypes.folder')
              : item.creationType
                ? t(CREATION_TYPE_TO_I18N_KEY[item.creationType])
                : t(CREATION_TYPE_TO_I18N_KEY.video)}
          </div>

          {/* Date */}
          <div className="flex items-center justify-between sm:hidden">
            <span className="text-sm">
              {format.dateTime(new Date(item.createdAt), {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              })}
            </span>
          </div>
        </div>
      </FileContextMenu>
    </DraggableItem>
  );
};
